import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Loader2, Search, ArrowLeft, Filter, Printer, Plus, Scan } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_COLORS = {
  in_stock: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  assigned: 'bg-sky-50 text-sky-700 border-sky-200',
  maintenance: 'bg-amber-50 text-amber-700 border-amber-200',
  retired: 'bg-slate-50 text-slate-600 border-slate-200',
  disposed: 'bg-slate-50 text-slate-500 border-slate-200',
  lost: 'bg-rose-50 text-rose-700 border-rose-200',
};

export default function AssetsListPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());

  const filters = {
    status: params.get('status') || '',
    category_id: params.get('category_id') || '',
    assigned_to_user_id: params.get('assigned_to_user_id') || '',
  };
  const setFilter = (k, v) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v); else n.delete(k);
    setParams(n, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
      if (search.trim()) q.set('search', search.trim());
      const r = await axios.get(`${API_URL}/api/assets?${q}`, { headers: authHeader() });
      setAssets(r.data.assets || []);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, search]);

  useEffect(() => {
    axios.get(`${API_URL}/api/assets/categories`, { headers: authHeader() }).then(r => setCategories(r.data.categories || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleAll = (e) => setSelected(e.target.checked ? new Set(assets.map(a => a.id)) : new Set());
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const printSelected = () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected).join(',');
    const t = localStorage.getItem('savenes_token');
    window.open(`${API_URL}/api/assets/labels.pdf?ids=${ids}&token=${t}`, '_blank');
    // Some browsers strip query auth; better to fetch as blob:
    axios.get(`${API_URL}/api/assets/labels.pdf?ids=${ids}`,
              { headers: authHeader(), responseType: 'blob' })
      .then(res => {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a'); a.href = url; a.download = 'asset-labels.pdf'; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      });
  };

  const parentCats = categories.filter(c => !c.parent_id);

  return (
    <div className="p-6 space-y-4" data-testid="assets-list-page">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/assets" className="text-sm text-[#0E7490] hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Workspace
          </Link>
          <h1 className="text-2xl font-bold text-[#0A2342] mt-1">Assets</h1>
          <p className="text-sm text-[#64748B]">{assets.length} asset{assets.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/assets/scan"><Button variant="outline" size="sm" data-testid="list-scan-btn"><Scan className="w-4 h-4 mr-2" /> Scan</Button></Link>
          {selected.size > 0 && (
            <Button onClick={printSelected} variant="outline" size="sm" data-testid="print-labels-btn">
              <Printer className="w-4 h-4 mr-2" /> Print {selected.size} label{selected.size !== 1 ? 's' : ''}
            </Button>
          )}
          <Link to="/assets/new"><Button size="sm" className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white"><Plus className="w-4 h-4 mr-2" /> New</Button></Link>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex flex-wrap items-center gap-3">
        <Filter className="w-4 h-4 text-[#64748B]" />
        <select value={filters.status} onChange={e => setFilter('status', e.target.value)}
                className="text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white" data-testid="filter-status">
          <option value="">All statuses</option>
          {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select value={filters.category_id} onChange={e => setFilter('category_id', e.target.value)}
                className="text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white" data-testid="filter-category">
          <option value="">All categories</option>
          {parentCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2 min-w-[260px]">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <Input placeholder="Search tag / name / serial…" value={search} onChange={e => setSearch(e.target.value)}
                 className="h-8 text-sm" data-testid="search-input" />
        </div>
      </div>

      {loading ? <div className="text-center py-12 text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        : assets.length === 0 ? <div className="text-center py-16 bg-white rounded-xl border border-[#E2E8F0] text-[#64748B]">No assets found.</div>
          : (
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr className="text-left text-xs font-semibold text-[#64748B] uppercase tracking-wide">
                <th className="px-3 py-3 w-8"><input type="checkbox" checked={selected.size === assets.length && assets.length > 0} onChange={toggleAll} /></th>
                <th className="px-4 py-3">Tag</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3">Assigned to</th><th className="px-4 py-3">Cost</th>
              </tr>
            </thead>
            <tbody>
              {assets.map(a => (
                <tr key={a.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]"
                    data-testid={`asset-row-${a.id}`}>
                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleOne(a.id)} data-testid={`select-${a.id}`} />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[#0E7490] cursor-pointer" onClick={() => navigate(`/assets/${a.id}`)}>{a.asset_tag}</td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/assets/${a.id}`)}>
                    <div className="text-[#0A2342] font-medium">{a.name}</div>
                    {a.serial_number && <div className="text-[10px] text-[#94A3B8] font-mono">S/N {a.serial_number}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs cursor-pointer" onClick={() => navigate(`/assets/${a.id}`)}>{a.category_name || '—'}</td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/assets/${a.id}`)}>
                    <Badge className={`text-[10px] px-1.5 py-0 ${STATUS_COLORS[a.status]}`}>{(a.status || '').replace('_', ' ')}</Badge>
                  </td>
                  <td className="px-4 py-3 text-xs cursor-pointer" onClick={() => navigate(`/assets/${a.id}`)}>
                    {a.assigned_to_user_name || a.assigned_to_patient_name || <span className="text-[#94A3B8]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#64748B] cursor-pointer" onClick={() => navigate(`/assets/${a.id}`)}>
                    {a.purchase_cost ? `$${a.purchase_cost.toLocaleString()}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
